import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../shared/components/PageHeader';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import {
  fetchAllIfrsStandards,
  createIfrsStandard,
  updateIfrsStandard,
  deactivateIfrsStandard,
  activateIfrsStandard,
  syncIfrsStandardsFromJson
} from '../../../services/ifrsStandards';
import './IfrsStandardsAdminPage.css';

const UI_TEXT = {
  'tr-TR': {
    pageTitle: 'IFRS Standartları',
    pageDescription: 'KGK (kgk.gov.tr) kaynaklı TMS/TFRS standartlarını yönetin.',
    newStandard: 'Yeni Standart',
    editStandard: 'Standardı Düzenle',
    code: 'Kod',
    body: 'Gövde',
    titleTr: 'Türkçe Başlık',
    titleEn: 'İngilizce Başlık',
    effectiveDate: 'Yürürlük Tarihi',
    kgkLink: 'KGK Bağlantısı',
    status: 'Durum',
    actions: 'İşlemler',
    active: 'Aktif',
    passive: 'Pasif',
    edit: 'Düzenle',
    deactivate: 'Pasifleştir',
    activate: 'Aktifleştir',
    save: 'Kaydet',
    cancel: 'İptal',
    saving: 'Kaydediliyor...',
    noRecords: 'Standart bulunamadı.',
    loading: 'Yükleniyor...',
    standardCode: 'Standart Kodu',
    standardBody: 'Standart Türü',
    numberCode: 'Numara',
    summaryTr: 'Türkçe Özet',
    summaryEn: 'İngilizce Özet',
    kgkUrl: 'KGK URL',
    pdfUrlTr: 'PDF URL (TR)',
    supersededBy: 'Yerine Geçen',
    syncTitle: 'KGK Senkronize Et',
    syncDescription: 'Aşağıya KGK\'dan alınan standartları JSON dizisi olarak yapıştırın ve senkronize edin.',
    syncButton: 'Senkronize Et',
    syncing: 'Senkronize ediliyor...',
    syncResult: 'Senkronizasyon Sonucu',
    syncCreated: 'Oluşturulan',
    syncUpdated: 'Güncellenen',
    syncSkipped: 'Atlanan (Hata)',
    lastSynced: 'Son Senkronizasyon',
    showSync: 'Senkronizasyon Paneli',
    hideSync: 'Gizle',
    requiredFields: 'Kod, tür, numara ve başlıklar zorunludur.',
    open: 'Aç',
  },
  'en-US': {
    pageTitle: 'IFRS Standards',
    pageDescription: 'Manage TMS/TFRS standards sourced from KGK (kgk.gov.tr).',
    newStandard: 'New Standard',
    editStandard: 'Edit Standard',
    code: 'Code',
    body: 'Body',
    titleTr: 'Turkish Title',
    titleEn: 'English Title',
    effectiveDate: 'Effective Date',
    kgkLink: 'KGK Link',
    status: 'Status',
    actions: 'Actions',
    active: 'Active',
    passive: 'Inactive',
    edit: 'Edit',
    deactivate: 'Deactivate',
    activate: 'Activate',
    save: 'Save',
    cancel: 'Cancel',
    saving: 'Saving...',
    noRecords: 'No standards found.',
    loading: 'Loading...',
    standardCode: 'Standard Code',
    standardBody: 'Standard Body',
    numberCode: 'Number',
    summaryTr: 'Turkish Summary',
    summaryEn: 'English Summary',
    kgkUrl: 'KGK URL',
    pdfUrlTr: 'PDF URL (TR)',
    supersededBy: 'Superseded By',
    syncTitle: 'Sync from KGK',
    syncDescription: 'Paste standards as a JSON array from KGK and synchronize.',
    syncButton: 'Synchronize',
    syncing: 'Synchronizing...',
    syncResult: 'Sync Result',
    syncCreated: 'Created',
    syncUpdated: 'Updated',
    syncSkipped: 'Skipped (Errors)',
    lastSynced: 'Last Synced',
    showSync: 'Sync Panel',
    hideSync: 'Hide',
    requiredFields: 'Code, body, number and titles are required.',
    open: 'Open',
  }
};

const EMPTY_FORM = {
  standardCode: '',
  standardBody: 'IAS',
  numberCode: '',
  titleTr: '',
  titleEn: '',
  effectiveDate: '',
  supersededBy: '',
  summaryTr: '',
  summaryEn: '',
  kgkUrl: '',
  pdfUrlTr: '',
  active: true
};

const BODY_OPTIONS = ['IAS', 'IFRS', 'IFRIC', 'SIC'];

export default function IfrsStandardsAdminPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = UI_TEXT[language] || UI_TEXT['tr-TR'];

  const authRole = sessionStorage.getItem('authRole');
  const isAuthorized = authRole === 'SYS_ADMIN';

  const [standards, setStandards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [showSync, setShowSync] = useState(false);
  const [syncJson, setSyncJson] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [filterBody, setFilterBody] = useState('');
  const [filterActive, setFilterActive] = useState('all');

  const loadStandards = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAllIfrsStandards();
      setStandards(data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthorized) {
      navigate('/');
    }
  }, [isAuthorized, navigate]);

  useEffect(() => { loadStandards(); }, [loadStandards]);

  if (!isAuthorized) {
    return null;
  }

  const clearFeedback = () => { setMessage(''); setError(''); };

  const handleEditClick = (std) => {
    setEditingId(std.id);
    setForm({
      standardCode: std.standardCode || '',
      standardBody: std.standardBody || 'IAS',
      numberCode: std.numberCode || '',
      titleTr: std.titleTr || '',
      titleEn: std.titleEn || '',
      effectiveDate: std.effectiveDate || '',
      supersededBy: std.supersededBy || '',
      summaryTr: std.summaryTr || '',
      summaryEn: std.summaryEn || '',
      kgkUrl: std.kgkUrl || '',
      pdfUrlTr: std.pdfUrlTr || '',
      active: std.active !== false
    });
    setShowForm(true);
    clearFeedback();
  };

  const handleNewClick = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
    clearFeedback();
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.standardCode.trim() || !form.standardBody.trim() || !form.numberCode.trim() || !form.titleTr.trim() || !form.titleEn.trim()) {
      setError(t.requiredFields);
      return;
    }
    setSaving(true);
    clearFeedback();
    try {
      const payload = {
        ...form,
        effectiveDate: form.effectiveDate || null,
        supersededBy: form.supersededBy || null,
        summaryTr: form.summaryTr || null,
        summaryEn: form.summaryEn || null,
        kgkUrl: form.kgkUrl || null,
        pdfUrlTr: form.pdfUrlTr || null
      };
      if (editingId) {
        await updateIfrsStandard(editingId, payload);
        setMessage(language === 'tr-TR' ? 'Standart güncellendi.' : 'Standard updated.');
      } else {
        await createIfrsStandard(payload);
        setMessage(language === 'tr-TR' ? 'Standart oluşturuldu.' : 'Standard created.');
      }
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      await loadStandards();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id) => {
    clearFeedback();
    try {
      await deactivateIfrsStandard(id);
      setMessage(language === 'tr-TR' ? 'Standart pasifleştirildi.' : 'Standard deactivated.');
      await loadStandards();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleActivate = async (id) => {
    clearFeedback();
    try {
      await activateIfrsStandard(id);
      setMessage(language === 'tr-TR' ? 'Standart aktifleştirildi.' : 'Standard activated.');
      await loadStandards();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    clearFeedback();
    try {
      const parsed = JSON.parse(syncJson);
      const result = await syncIfrsStandardsFromJson(Array.isArray(parsed) ? parsed : [parsed]);
      setSyncResult(result);
      setMessage(language === 'tr-TR' ? 'Senkronizasyon tamamlandı.' : 'Synchronization completed.');
      await loadStandards();
    } catch (e) {
      setError(e.message || (language === 'tr-TR' ? 'Geçersiz JSON veya senkronizasyon hatası.' : 'Invalid JSON or sync error.'));
    } finally {
      setSyncing(false);
    }
  };

  const filteredStandards = standards.filter(s => {
    if (filterBody && s.standardBody !== filterBody) return false;
    if (filterActive === 'active' && !s.active) return false;
    if (filterActive === 'inactive' && s.active) return false;
    return true;
  });

  return (
    <div className="ifrs-std-page">
      <PageHeader
        title={t.pageTitle}
        description={t.pageDescription}
      />

      {message && <div className="ifrs-std-banner-success">{message}</div>}
      {error   && <div className="ifrs-std-banner-error">{error}</div>}

      {/* Toolbar */}
      <div className="ifrs-std-toolbar">
        <div className="ifrs-std-filters">
          <select value={filterBody} onChange={e => setFilterBody(e.target.value)}>
            <option value="">{language === 'tr-TR' ? 'Tüm Türler' : 'All Bodies'}</option>
            {BODY_OPTIONS.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <select value={filterActive} onChange={e => setFilterActive(e.target.value)}>
            <option value="all">{language === 'tr-TR' ? 'Tüm Durumlar' : 'All Statuses'}</option>
            <option value="active">{t.active}</option>
            <option value="inactive">{t.passive}</option>
          </select>
        </div>
        <div className="ifrs-std-actions">
          <button className="ifrs-std-btn-secondary" onClick={() => { setShowSync(v => !v); clearFeedback(); }}>
            {showSync ? t.hideSync : t.showSync}
          </button>
          <button className="ifrs-std-btn-primary" onClick={handleNewClick}>
            + {t.newStandard}
          </button>
        </div>
      </div>

      {/* KGK Sync Panel */}
      {showSync && (
        <div className="ifrs-std-card">
          <h3>{t.syncTitle}</h3>
          <p className="ifrs-std-hint">{t.syncDescription}</p>
          <textarea
            className="ifrs-std-sync-textarea"
            value={syncJson}
            onChange={e => setSyncJson(e.target.value)}
            rows={10}
            placeholder='[{"standardCode":"IAS 1","standardBody":"IAS",...}]'
          />
          <div className="ifrs-std-actions" style={{ marginTop: '8px' }}>
            <button className="ifrs-std-btn-primary" onClick={handleSync} disabled={syncing || !syncJson.trim()}>
              {syncing ? t.syncing : t.syncButton}
            </button>
          </div>
          {syncResult && (
            <div className="ifrs-std-sync-result">
              <strong>{t.syncResult}:</strong>
              <span className="ifrs-std-sync-badge green">{t.syncCreated}: {syncResult.created}</span>
              <span className="ifrs-std-sync-badge blue">{t.syncUpdated}: {syncResult.updated}</span>
              {syncResult.skipped > 0 && <span className="ifrs-std-sync-badge red">{t.syncSkipped}: {syncResult.skipped}</span>}
              {syncResult.errors && syncResult.errors.length > 0 && (
                <ul className="ifrs-std-sync-errors">
                  {syncResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {/* Create / Edit Form */}
      {showForm && (
        <div className="ifrs-std-card">
          <h3>{editingId ? t.editStandard : t.newStandard}</h3>
          <div className="ifrs-std-form-grid">
            <label>
              {t.standardCode} *
              <input value={form.standardCode} onChange={e => handleFormChange('standardCode', e.target.value)} placeholder="IAS 1" maxLength={32} />
            </label>
            <label>
              {t.standardBody} *
              <select value={form.standardBody} onChange={e => handleFormChange('standardBody', e.target.value)}>
                {BODY_OPTIONS.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </label>
            <label>
              {t.numberCode} *
              <input value={form.numberCode} onChange={e => handleFormChange('numberCode', e.target.value)} placeholder="1" maxLength={8} />
            </label>
            <label>
              {t.effectiveDate}
              <input type="date" value={form.effectiveDate} onChange={e => handleFormChange('effectiveDate', e.target.value)} />
            </label>
            <label className="ifrs-std-full">
              {t.titleTr} *
              <input value={form.titleTr} onChange={e => handleFormChange('titleTr', e.target.value)} placeholder="TMS 1 - Finansal Tabloların Sunuluşu" maxLength={512} />
            </label>
            <label className="ifrs-std-full">
              {t.titleEn} *
              <input value={form.titleEn} onChange={e => handleFormChange('titleEn', e.target.value)} placeholder="IAS 1 - Presentation of Financial Statements" maxLength={512} />
            </label>
            <label className="ifrs-std-full">
              {t.kgkUrl}
              <input value={form.kgkUrl} onChange={e => handleFormChange('kgkUrl', e.target.value)} placeholder="https://www.kgk.gov.tr/tr/icerik/tms-1" maxLength={1024} />
            </label>
            <label className="ifrs-std-full">
              {t.pdfUrlTr}
              <input value={form.pdfUrlTr} onChange={e => handleFormChange('pdfUrlTr', e.target.value)} placeholder="https://..." maxLength={1024} />
            </label>
            <label>
              {t.supersededBy}
              <input value={form.supersededBy} onChange={e => handleFormChange('supersededBy', e.target.value)} placeholder="IFRS 9" maxLength={32} />
            </label>
            <label>
              {t.status}
              <select value={form.active ? 'active' : 'inactive'} onChange={e => handleFormChange('active', e.target.value === 'active')}>
                <option value="active">{t.active}</option>
                <option value="inactive">{t.passive}</option>
              </select>
            </label>
            <label className="ifrs-std-full">
              {t.summaryTr}
              <textarea rows={3} value={form.summaryTr} onChange={e => handleFormChange('summaryTr', e.target.value)} />
            </label>
            <label className="ifrs-std-full">
              {t.summaryEn}
              <textarea rows={3} value={form.summaryEn} onChange={e => handleFormChange('summaryEn', e.target.value)} />
            </label>
          </div>
          <div className="ifrs-std-actions" style={{ marginTop: '12px' }}>
            <button className="ifrs-std-btn-secondary" onClick={() => { setShowForm(false); setEditingId(null); clearFeedback(); }}>
              {t.cancel}
            </button>
            <button className="ifrs-std-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? t.saving : t.save}
            </button>
          </div>
        </div>
      )}

      {/* Standards Table */}
      <div className="ifrs-std-card">
        {loading ? (
          <p className="ifrs-std-hint">{t.loading}</p>
        ) : filteredStandards.length === 0 ? (
          <p className="ifrs-std-hint">{t.noRecords}</p>
        ) : (
          <div className="ifrs-std-table-wrap">
            <table className="ifrs-std-table">
              <thead>
                <tr>
                  <th>{t.code}</th>
                  <th>{t.body}</th>
                  <th>{t.titleTr}</th>
                  <th>{t.titleEn}</th>
                  <th>{t.effectiveDate}</th>
                  <th>{t.kgkLink}</th>
                  <th>{t.lastSynced}</th>
                  <th>{t.status}</th>
                  <th>{t.actions}</th>
                </tr>
              </thead>
              <tbody>
                {filteredStandards.map(std => (
                  <tr key={std.id} className={std.active ? '' : 'ifrs-std-row-inactive'}>
                    <td><strong>{std.standardCode}</strong></td>
                    <td><span className="ifrs-std-body-badge">{std.standardBody}</span></td>
                    <td className="ifrs-std-title-cell">{std.titleTr}</td>
                    <td className="ifrs-std-title-cell">{std.titleEn}</td>
                    <td>{std.effectiveDate || '—'}</td>
                    <td>
                      {std.kgkUrl
                        ? <a href={std.kgkUrl} target="_blank" rel="noopener noreferrer" className="ifrs-std-link">{t.open} ↗</a>
                        : '—'}
                    </td>
                    <td>{std.lastSyncedAt ? std.lastSyncedAt.substring(0, 10) : '—'}</td>
                    <td>
                      <span className={`ifrs-std-status-badge ${std.active ? 'active' : 'inactive'}`}>
                        {std.active ? t.active : t.passive}
                      </span>
                    </td>
                    <td>
                      <div className="ifrs-std-row-actions">
                        <button className="ifrs-std-btn-xs" onClick={() => handleEditClick(std)}>{t.edit}</button>
                        {std.active
                          ? <button className="ifrs-std-btn-xs danger" onClick={() => handleDeactivate(std.id)}>{t.deactivate}</button>
                          : <button className="ifrs-std-btn-xs" onClick={() => handleActivate(std.id)}>{t.activate}</button>
                        }
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
